import { Quaternion, Vector3, type Object3D } from 'three/webgpu';
import {
  AssemblyComponentFixationService,
  AssemblyComponentFixationState,
} from '../component-fixation';
import type {
  AssemblyTransformHistoryOperation,
  AssemblyTransformHistoryRecord,
  ComponentInstanceSnapshot,
  ComponentTransformSnapshot,
} from './ComponentInstanceTypes';

/**
 * 组件实例快照创建输入。
 */
export interface CreateComponentInstanceSnapshotInput {
  /** Three.js 场景对象。 */
  readonly object: Object3D;
  /** 可选装配体 ID，未传时从 metadata / 父节点 / 默认值推导。 */
  readonly assemblyId?: string;
  /** 可选组件实例 ID，未传时从 metadata / uuid 推导。 */
  readonly componentInstanceId?: string;
  /** 可选来源节点 ID，未传时从 metadata / uuid 推导。 */
  readonly sourceNodeId?: string;
  /** 可选来源文件 UUID。 */
  readonly sourcePartFileUuid?: string;
  /** 可选父组件实例 ID。 */
  readonly parentComponentInstanceId?: string;
  /** 是否被抑制。 */
  readonly suppressed?: boolean;
}

/**
 * 组件实例快照比较选项。
 */
export interface CompareComponentInstanceSnapshotOptions {
  /** 数值比较容差，缺省使用严格 CAD 位姿比较容差。 */
  readonly epsilon?: number;
}

/**
 * 从未知对象上读取字符串属性。
 *
 * @param record 普通记录。
 * @param keys 候选属性名。
 * @returns 第一个可用字符串。
 */
function readStringValue(
  record: Record<string, unknown> | undefined,
  keys: readonly string[],
): string | undefined {
  if (!record) {
    return undefined;
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  return undefined;
}

/**
 * 从未知对象上读取布尔属性。
 *
 * @param record 普通记录。
 * @param keys 候选属性名。
 * @returns 第一个可用布尔值。
 */
function readBooleanValue(
  record: Record<string, unknown> | undefined,
  keys: readonly string[],
): boolean | undefined {
  if (!record) {
    return undefined;
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }

  return undefined;
}

/**
 * 从普通记录中读取普通对象字段。
 *
 * @param record 普通记录。
 * @param key 字段名。
 * @returns 字段值为普通对象时返回该对象，否则返回空记录。
 */
function readRecordValue(record: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = record[key];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

/**
 * 读取 Object3D metadata。
 *
 * @param object Three.js 场景对象。
 * @returns metadata 普通记录。
 */
function readObjectMetadata(object: Object3D | null | undefined): Record<string, unknown> | undefined {
  if (!object) {
    return undefined;
  }

  const rawUserData = object.userData;
  const rawMetadata = object.metadata;
  const userData = rawUserData && typeof rawUserData === 'object'
    ? rawUserData as Record<string, unknown>
    : {};
  const metadata = rawMetadata && typeof rawMetadata === 'object'
    ? rawMetadata as Record<string, unknown>
    : {};
  const importedModel = readRecordValue(metadata, 'importedModel');
  const importedHierarchy = readRecordValue(importedModel, 'hierarchy');
  const importedHierarchyMeta = readRecordValue(importedHierarchy, 'meta');
  const importedSelectionTarget = readRecordValue(importedModel, 'selectionTarget');
  const glbHierarchy = userData.glbHierarchy && typeof userData.glbHierarchy === 'object'
    ? userData.glbHierarchy as Record<string, unknown>
    : {};
  const glbHierarchyMeta = glbHierarchy.meta && typeof glbHierarchy.meta === 'object'
    ? glbHierarchy.meta as Record<string, unknown>
    : {};

  /**
   * imported-model 的权威语义多数挂在 userData / glbHierarchy 上；
   * 普通建模对象仍可能使用 metadata。这里统一投影成只读记录，
   * 避免移动历史、保存和 STEP 导出继续使用不同 ID 体系。
   */
  return {
    ...importedHierarchyMeta,
    ...importedHierarchy,
    ...importedSelectionTarget,
    ...importedModel,
    ...glbHierarchyMeta,
    ...glbHierarchy,
    ...userData,
    ...metadata,
  };
}

/**
 * 从后代对象中读取唯一的字符串语义值。
 *
 * 设计说明：
 * 项目模型版本切换后，真实可写 transform 根可能只是一个普通 Group，只有它的可见 Mesh 后代保留
 * `assemblyNodeId`。Move/Rotate 必须继续使用稳定装配节点 ID 作为 Mate 求解锚点，不能退回运行时 uuid。
 * 只有所有后代解析出的值完全一致时才采用该值，避免多零件共享父节点时误把父 Group 绑定到某个子零件。
 *
 * @param object 需要补全身份的 transform 根对象。
 * @param keys 候选 metadata 字段名。
 * @returns 唯一稳定值；缺失或存在歧义时返回 undefined。
 */
function readUniqueDescendantStringValue(
  object: Object3D,
  keys: readonly string[],
): string | undefined {
  let uniqueValue: string | undefined;
  let ambiguous = false;

  object.traverse((child) => {
    if (child === object || ambiguous) {
      return;
    }

    const childValue = readStringValue(readObjectMetadata(child as Object3D), keys);
    if (!childValue) {
      return;
    }
    if (uniqueValue && uniqueValue !== childValue) {
      ambiguous = true;
      uniqueValue = undefined;
      return;
    }

    uniqueValue = childValue;
  });

  return ambiguous ? undefined : uniqueValue;
}

/**
 * 创建组件 transform 快照。
 *
 * @param object Three.js 场景对象。
 * @returns 可序列化 transform 快照。
 */
export function createComponentTransformSnapshot(object: Object3D): ComponentTransformSnapshot {
  return {
    position: [object.position.x, object.position.y, object.position.z],
    quaternion: [object.quaternion.x, object.quaternion.y, object.quaternion.z, object.quaternion.w],
    scale: [object.scale.x, object.scale.y, object.scale.z],
  };
}

/**
 * 创建组件 world transform 快照。
 *
 * 设计说明：
 * Mate 几何描述符来自导入拓扑命中链路，坐标已经是 world space。子装配父节点带 transform 时，
 * local 位姿无法作为几何变换基线；这里把 Object3D.matrixWorld 分解成纯数据快照，交给 cad-core
 * 求解器使用，避免业务层或 web-client 保存运行时 Object3D。
 *
 * @param object Three.js 场景对象。
 * @returns 可序列化 world transform 快照。
 */
export function createComponentWorldTransformSnapshot(object: Object3D): ComponentTransformSnapshot {
  object.updateWorldMatrix(true, false);
  const position = new Vector3();
  const quaternion = new Quaternion();
  const scale = new Vector3();
  object.matrixWorld.decompose(position, quaternion, scale);

  return {
    position: [position.x, position.y, position.z],
    quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
    scale: [scale.x, scale.y, scale.z],
  };
}

/**
 * 创建组件实例快照。
 *
 * @param input 创建输入。
 * @returns 规范化组件实例快照。
 */
export function createComponentInstanceSnapshotFromObject(
  input: CreateComponentInstanceSnapshotInput,
): ComponentInstanceSnapshot {
  const metadata = readObjectMetadata(input.object);
  const parentMetadata = readObjectMetadata(input.object.parent ?? undefined);
  const componentInstanceId = input.componentInstanceId
    ?? readStringValue(metadata, ['componentInstanceId', 'assemblyComponentInstanceId', 'assemblyNodeId', 'sourceNodeId', 'nodeId', 'objectId'])
    ?? readUniqueDescendantStringValue(input.object, ['componentInstanceId', 'assemblyComponentInstanceId', 'assemblyNodeId', 'sourceNodeId', 'nodeId', 'objectId'])
    ?? input.object.uuid;
  const assemblyId = input.assemblyId
    ?? readStringValue(metadata, ['assemblyId', 'rootAssemblyId', 'documentAssemblyId', 'glbFileId', 'fileId'])
    ?? readStringValue(parentMetadata, ['assemblyId', 'rootAssemblyId', 'documentAssemblyId', 'glbFileId', 'fileId'])
    ?? readUniqueDescendantStringValue(input.object, ['assemblyId', 'rootAssemblyId', 'documentAssemblyId', 'glbFileId', 'fileId'])
    ?? 'default-assembly';
  const sourceNodeId = input.sourceNodeId
    ?? readStringValue(metadata, ['sourceNodeId', 'assemblyNodeId', 'nodeId', 'objectId'])
    ?? readUniqueDescendantStringValue(input.object, ['sourceNodeId', 'assemblyNodeId', 'nodeId', 'objectId'])
    ?? input.object.uuid;
  const sourcePartFileUuid = input.sourcePartFileUuid
    ?? readStringValue(metadata, ['sourcePartFileUuid', 'partFileUuid', 'externalFileUuid', 'fileUuid'])
    ?? readUniqueDescendantStringValue(input.object, ['sourcePartFileUuid', 'partFileUuid', 'externalFileUuid', 'fileUuid']);
  const parentComponentInstanceId = input.parentComponentInstanceId
    ?? readStringValue(parentMetadata, ['componentInstanceId', 'assemblyComponentInstanceId', 'assemblyNodeId']);
  const suppressed = input.suppressed
    ?? readBooleanValue(metadata, ['suppressed', 'isSuppressed'])
    ?? false;

  // 历史快照会进入 IndexedDB；可选字段缺失时必须直接省略键，避免 undefined 破坏 plain-data 校验。
  return {
    componentInstanceId,
    assemblyId,
    sourceNodeId,
    ...(sourcePartFileUuid === undefined ? {} : { sourcePartFileUuid }),
    ...(parentComponentInstanceId === undefined ? {} : { parentComponentInstanceId }),
    transform: createComponentTransformSnapshot(input.object),
    fixationState: AssemblyComponentFixationService.resolveSceneObjectFixationState(input.object),
    suppressed,
  };
}

/**
 * 将组件实例快照应用回 Three.js 对象。
 *
 * @param object Three.js 场景对象。
 * @param snapshot 组件实例快照。
 */
export function applyComponentInstanceSnapshotToObject(
  object: Object3D,
  snapshot: ComponentInstanceSnapshot,
): void {
  object.position.set(
    snapshot.transform.position[0],
    snapshot.transform.position[1],
    snapshot.transform.position[2],
  );
  object.quaternion.set(
    snapshot.transform.quaternion[0],
    snapshot.transform.quaternion[1],
    snapshot.transform.quaternion[2],
    snapshot.transform.quaternion[3],
  );
  object.scale.set(
    snapshot.transform.scale[0],
    snapshot.transform.scale[1],
    snapshot.transform.scale[2],
  );
  AssemblyComponentFixationService.writeSceneObjectFixationState(object, snapshot.fixationState);
  /**
   * 配合预览、拖动后重求解和导入拓扑高亮都会在同一帧内立刻读取 world matrix。
   * `updateMatrixWorld(true)` 只保证自身向下刷新，父装配节点若刚改变 local transform，
   * 仍可能把旧父矩阵传给 FeatureFace overlay；这里必须同时刷新父链和子树。
   */
  object.updateMatrix();
  object.updateWorldMatrix(true, true);
}

/**
 * 判断数值是否在容差内相等。
 *
 * @param left 左值。
 * @param right 右值。
 * @param epsilon 容差。
 * @returns 是否相等。
 */
function areNumbersEqual(left: number, right: number, epsilon: number): boolean {
  return Math.abs(left - right) <= epsilon;
}

/**
 * 判断数值元组是否在容差内相等。
 *
 * @param left 左元组。
 * @param right 右元组。
 * @param epsilon 容差。
 * @returns 是否相等。
 */
function areNumberTuplesEqual(
  left: readonly number[],
  right: readonly number[],
  epsilon: number,
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => areNumbersEqual(value, right[index], epsilon));
}

/**
 * 比较组件 transform 快照。
 *
 * @param left 左侧 transform。
 * @param right 右侧 transform。
 * @param options 比较选项。
 * @returns 是否语义相等。
 */
export function areComponentTransformSnapshotsEqual(
  left: ComponentTransformSnapshot,
  right: ComponentTransformSnapshot,
  options: CompareComponentInstanceSnapshotOptions = {},
): boolean {
  const epsilon = options.epsilon ?? 1e-9;

  return areNumberTuplesEqual(left.position, right.position, epsilon)
    && areNumberTuplesEqual(left.quaternion, right.quaternion, epsilon)
    && areNumberTuplesEqual(left.scale, right.scale, epsilon);
}

/**
 * 比较组件实例快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @param options 比较选项。
 * @returns 是否语义相等。
 */
export function areComponentInstanceSnapshotsEqual(
  left: ComponentInstanceSnapshot,
  right: ComponentInstanceSnapshot,
  options: CompareComponentInstanceSnapshotOptions = {},
): boolean {
  return left.componentInstanceId === right.componentInstanceId
    && left.assemblyId === right.assemblyId
    && left.sourceNodeId === right.sourceNodeId
    && left.sourcePartFileUuid === right.sourcePartFileUuid
    && left.parentComponentInstanceId === right.parentComponentInstanceId
    && left.fixationState === right.fixationState
    && left.suppressed === right.suppressed
    && areComponentTransformSnapshotsEqual(left.transform, right.transform, options);
}

/**
 * 创建装配体 transform 历史记录。
 *
 * @param operation 操作类型。
 * @param before 变更前快照。
 * @param after 变更后快照。
 * @returns 非空历史记录；无语义变化时返回 null。
 */
export function createAssemblyTransformHistoryRecord(
  operation: AssemblyTransformHistoryOperation,
  before: ComponentInstanceSnapshot,
  after: ComponentInstanceSnapshot,
): AssemblyTransformHistoryRecord | null {
  if (areComponentInstanceSnapshotsEqual(before, after)) {
    return null;
  }

  return {
    operation,
    before,
    after,
  };
}

